import QnAddReaction from "@assets/quick-news/likeIcon.svg";
import IcAddReaction from "@assets/reactions/add-reaction.svg";
import { reactionCount } from "@helper/utils";
import { ReactionProp } from "@typings/Reactions";
import { lazy, type ReactElement, Suspense } from "react";

// Lazy load Lottie powered animation to avoid including heavy library and animation data in main bundle
// - currently this means the animation will be loaded client-side only
const ReactionAnimation = lazy(
  () => import("@components/Blocks/AnimationPlayer/AnimationPlayer"),
);

export type ArticleReactionsProps = {
  reactions: ReactionProp[];
  reactionsUser: string;
  limit?: 1 | 2;
  referer?: string;
  variant?: "default" | "small";
  qnStyle?: boolean;
};

export const ArticleReactions = ({
  reactions,
  reactionsUser,
  limit = 2,
  referer = "article",
  variant = "default",
  qnStyle = false,
}: ArticleReactionsProps): ReactElement => {
  if (reactions.length === 0) {
    return (
      <span key={`reaction-empty-${referer}`} data-testid="no-reaction"></span>
    );
  }

  const totalReactions =
    reactions.reduce((sum, reaction) => sum + reaction.count, 0) || 0;

  if (totalReactions === 0) {
    return (
      <div
        className={`w-[40px] flex items-center gap-0.5 rounded-[50px] ${!qnStyle && "border border-brand-primary"} py-2 px-2 ${variant === "small" ? "h-8" : ""}`}
      >
        <img
          width={24}
          height={24}
          src={qnStyle ? QnAddReaction : IcAddReaction}
          data-testid="add-reaction"
          alt="add-reaction"
        />
      </div>
    );
  }

  const topReactions = [...reactions]
    .filter((reaction) => reaction.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  return (
    <div
      className={`w-fit flex items-center justify-center gap-0.5 rounded-[50px] ${!qnStyle && "border"} py-2.5 px-3
      ${!qnStyle && (reactionsUser && reactionsUser !== "0" ? "border-brand-primary" : "border-neutral-700")}
      ${variant === "small" ? "h-8" : ""}`}
    >
      <div className="flex items-center">
        {topReactions.map((reaction, index) => (
          <span
            key={`reactions-span-${referer}-${reaction.id}`}
            data-testid={`reaction-${index}`}
            role="img"
            aria-label={reaction.text}
            className={`w-5 h-5 ${index > 0 ? "-ml-1" : ""}`}
          >
            <Suspense fallback={null}>
              <ReactionAnimation src={`/reactions/${reaction.id}.json`} />
            </Suspense>
          </span>
        ))}
      </div>
      <span
        className={`text-base ${reactionsUser && reactionsUser !== "0" ? "text-brand-primary" : "text-neutral-700"} font-medium leading-[150%]`}
      >
        {reactionCount(totalReactions)}
      </span>
    </div>
  );
};
